<template>
    <v-form ref="forgotUsernameForm" v-model="valid" lazy-validation>
        <v-flex v-if="confirmedApiResponse && !isSubmitting">
            <div class="forgot-username__success">
                <div class="forgot-username__success-message">{{loginConstants.USERNAME_EMAIL_SUCCESS_1}}
                    <span>{{confirmedApiResponse.email}}</span> {{loginConstants.USERNAME_EMAIL_SUCCESS_2}}
                </div>
            </div>
            <v-btn
                id="resend_email_btn"
                data-qe-id="login-forgot-username_resend_email_btn"
                @click="resendRecoveryEmail"
                :disabled="isSubmitting"
                color="ypo_blue"
                flat>
                <v-icon color="ypo_blue_secondary" left>replay</v-icon>
                {{loginConstants.RESEND_EMAIL}}
            </v-btn>
        </v-flex>
        <v-flex v-else>
            <div class="forgot-username__title">{{loginConstants.EMAIL_ASSOCIATED_WITH_YPO}}</div>
        </v-flex>
        <v-flex v-if="!confirmedApiResponse">
            <v-text-field
                id="username_reset_email_input"
                data-qe-id="login-forgot-username_email_input"
                prepend-inner-icon="email"
                name="Email"
                autofocus
                validate-on-blur
                v-model="email"
                :placeholder="loginConstants.EMAIL_PLACEHOLDER_PASSWORD_RESET"
                :rules="emailRules"
                :disabled="isSubmitting"
                :onkeyup="resetError"
                required
                v-on:keydown.enter.prevent>
            </v-text-field>
            <v-flex class="forgot-links">
                <span
                    id="username_further_assistance_icon"
                    data-qe-id="login-forgot-username_further-assistance_btn"
                    @click="isExternalSpeaker ? changeCardState(loginConstants.componentNames.spousePartnerUsernameRecovery.component) : changeCardState(componentNames.usernameFurtherAssistance.component)"> 
                        {{loginConstants.EMAIL_RESET}}
                </span>
            </v-flex>
        </v-flex>
        <Login-error class="forgot-username__error"></Login-error>
        <v-flex class="username-assitance" v-if="!isExternalSpeaker">
            <span> {{loginConstants.USERNAME_ASSISTANCE_MAIL}}
            <em id="forgot-username-assistance" @click="launchEmailClient(loginConstants.YPO_HELP_EMAIL)"> {{loginConstants.YPO_HELP_EMAIL}} </em> </span>
        </v-flex>
        <v-card-actions>
            <v-layout row wrap>
                <v-btn
                    :flat="!confirmedApiResponse"
                    large
                    id="return_to_home_btn"
                    data-qe-id="return_to_home_btn"
                    block
                    :class="confirmedApiResponse ? 'white--text' : 'ypo_blue--text'"
                    :color="confirmedApiResponse ? 'ypo_blue' : ''"
                    @click="returnToLogin"
                    :disabled="isSubmitting">
                    {{!confirmedApiResponse ? loginConstants.CANCEL : loginConstants.RETURN_TO_LOGIN}}
                </v-btn>
                <v-btn
                    v-if="!confirmedApiResponse"
                    id="submit_username_recovery_btn"
                    color="ypo_blue"
                    class="white--text"
                    @click="sendRecoveryEmail"
                    :disabled="!email.length || !valid || isSubmitting"
                    large
                    block>
                    {{loginConstants.SUBMIT}}
                </v-btn>
            </v-layout>
        </v-card-actions>
    </v-form>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import { storeGetters } from "../../../store/storeGetters.constant";
    import { storeAction } from "../../../store/storeActions.constant";
    import { loginConstants } from '../login.constants';
    import LoginError from '../LoginError';
    import {gaDataLayerEvent, gaPageViewEvent} from "@/plugins/analytics";
    import { gtmEventName } from "@/plugins/gtmAnalytics";

    export default {
        name: 'ForgotUsername',
        components: { LoginError },
        data: () => ({
            loginConstants,
            valid: true,
            componentNames: loginConstants.componentNames,
            email: '',
            confirmedApiResponse: null,
            emailRules: [
                v => !!v || loginConstants.errors.EMAIL_REQUIRED,
                v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || loginConstants.errors.INVALID_EMAIL,
            ],
        }),
        props: {
            changeCardState: Function,
            toggleProgressIndicator: Function,
            isSubmitting: Boolean,
            returnToLogin: Function,
            skipFormValidation: {
                type: Boolean,
                default: false,
            },
        },
        methods: {
            ...mapActions({
                submitUsernameRecoveryEmail: `auth/${storeAction.submitUsernameRecoveryEmail}`,
                clearErrorOnStateChange: `auth/${storeAction.handleLoginErrorMessage}`,
                initiateLoginRedirect: `auth/${storeAction.initiateLoginRedirect}`,
            }),
            submit() {
                this.toggleProgressIndicator();
                this.clearErrorOnStateChange(null);
                if (this.skipFormValidation || this.$refs.forgotUsernameForm.validate()) {
                    this.submitUsernameRecoveryEmail(this.email)
                        .then((resp) => {
                            this.toggleProgressIndicator();
                            this.confirmedApiResponse = resp.data;
                            gaPageViewEvent(gtmEventName.C_Page_View, window.location.hash, gtmEventName.C_Login_RecoverUN_Acknowledged_Page_View);
                        })
                        .catch(() => {
                            this.toggleProgressIndicator();
                        });
                }
            },
            sendRecoveryEmail() {
                this.submit();
                gaDataLayerEvent(gtmEventName.C_Login_RecoverUN_Continue, this.email);
            },
            resendRecoveryEmail() {
                this.submit();
                gaDataLayerEvent(gtmEventName.C_Login_RecoverUN_ResendEmail, this.email);
            },
            resetError() {
                if (!this.email.length) { this.clearErrorOnStateChange(null); }
            },
            launchEmailClient(email) {
                gaDataLayerEvent(gtmEventName.C_Login_RecoverUN_ContactConcierge, '');
                window.location = `mailto:${email}`;
            },
        },
        computed: {
            ...mapGetters({
                recoveryData: storeGetters.recoveryData,
                isExternalSpeaker: `auth/${storeGetters.isExternalSpeaker}`,
            }),
        },
    };
</script>

<style lang="scss" scoped>
    @import '../../../common/styles/_colors.scss';
    @import '../../../common/styles/_responsive.scss';

    .forgot-username {
        &__title {
            padding: 10px 0 5px;
            font-size: 15px;
            color: $black;
            opacity: 0.9;
            font-family: 'Noto Sans', sans-serif;
        }

        &__success {
            padding: 10px 5px;
            /*border-top: 1px solid $ypo-gray;*/
            display: flex;
            flex-wrap: wrap;

            &-message {
                color: $ypo-blue;
            }

            &-email {
                color: $ypo-gold;
                padding-left: 5px;
                word-break: break-all;
            }
        }

        &__error {
            padding: 0 5px 10px;
        }

        &__resend {
            color: $ypo-blue-secondary;
        }
    }

    .v-btn__content {
        @include breakpoint(xs) {
            font-size: 12px !important;
        }
    }

    .v-card__actions {
        padding: 0 !important;

        @include breakpoint(xs) {
            padding: 0 !important;
        }

    }

    .forgot-links {
        span {
            color: $ypo-cta !important;
            cursor: pointer;
        }
    }

    .username-assitance {
        margin: 40px 0 50px 0;
        em {
            color: $ypo-cta !important;
            cursor: pointer;
            font-style: normal;            
        }
    }
</style>
